import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFloating, offset, shift, arrow, FloatingArrow, autoUpdate, flip, UseFloatingOptions } from '@floating-ui/react';
import { delay } from 'shared/promise';
import { entries, keyBy } from 'shared/collection';
import { isNullOrEmpty } from 'shared/collection';
import { StyleProps } from 'shared/ui/uiType';
import { DarkSchemedChildren, Markdown, useMemoValue, useTip } from 'shared/ui';
import { ActionIcon, Button, CloseButton, Tabs, Tooltip } from '@mantine/core';
import { useHotkeys, useSessionStorage } from '@mantine/hooks';
import { useElements } from 'shared/ui/useElements';
import { match, P } from 'ts-pattern';
import { HiChevronLeft, HiChevronRight, HiExternalLink } from 'react-icons/hi';
import { nav } from './nav';
import { KeyboardEventKey } from 'components/Keyboard';
import { pipe, unique } from 'remeda';
import { tourCfg, TourSettings, TourId } from './tourTypes';
import { InfoTip } from 'components/Info';
import { twMerge } from 'tailwind-merge';
export * from './tourTypes';
export type TipMd = {
  title: string;
  text: string;
  more?: string;
  placement?: UseFloatingOptions['placement'];
  video?: string;
};
export type UseTour<TipId extends string = string> = ReturnType<typeof useTour<TipId>>;
export type TourCfg<TipId extends string = string> = Record<TipId, TipMd>;
const scrollIntoViewWidthPad = (el: Element, {
  pad,
  ...options
}: ScrollIntoViewOptions & {
  pad: string;
}) => {
  // Create a new div element to act as a spacer
  const spacer = document.createElement('div');

  // Set the height and absolute positioning of the spacer
  spacer.style.position = 'absolute';
  spacer.style.top = `-${pad}`;
  spacer.style.bottom = `-${pad}`;
  spacer.style.left = '0';
  spacer.style.right = '0';
  spacer.style.height = `calc(100% + ${pad + pad}px)`;
  spacer.style.visibility = 'hidden'; // Make the spacer invisible

  el.appendChild(spacer);

  // Scroll to the spacer, effectively adding padding above and below the target element
  spacer.scrollIntoView(options);
  setTimeout(() => {
    el.removeChild(spacer);
  }, 1000);
};
export const useTour = <TipId extends string,>(tourCfg: TourCfg<TipId>, options: {
  disabled?: boolean;
  settings: TourSettings;
  onSeen: (id: TipId, mode: 'seen' | 'un-see') => void;
  onDisable: () => void;
  onReset: () => void;
}) => {
  const settings = useMemoValue(options.settings);
  const [openState, setOpen] = useState<Boolean | undefined>();
  const [initialSeen, setInitialSeen] = useState(settings.seen);
  const [shownTips, setShownTips] = useSessionStorage<TipId[]>({
    key: 'tour.shownTips',
    defaultValue: []
  }); // filter out the original seen list, but keep track of the current states seen
  const {
    tipsList: tipsList_0,
    tips
  } = useMemo(() => {
    const tipsList = entries(tourCfg).filter(([k]) => !initialSeen.includes(k as any)).map(([id, md]) => ({
      id,
      ...md,
      seen: shownTips.includes(id) || settings.seen.includes(id as any)
    }));
    return {
      tipsList,
      tips: keyBy(tipsList, t => t.id)
    };
  }, [tourCfg, shownTips, settings]);

  // wait about 1second after any url path changes to show the tour, evaluate the elements to show so we only get the relevant ones
  const [loadState, setLoadState] = useState<'waiting' | 'wait-for-el' | 'enable'>('waiting');

  // go though load states so we can fade into visibility in the right spot
  useEffect(() => {
    match(loadState).with('waiting', () => delay(1000).then(() => setLoadState('wait-for-el'))).otherwise(() => {});
  }, [loadState]);
  const {
    elById
  } = useElements(loadState == 'waiting' ? [] : tipsList_0.map(t_0 => t_0.id), {
    getEl: id_0 => document.querySelector(`[data-tip-id="${id_0}"]`)
  });
  const validTips = useMemo(() => tipsList_0.map(t_1 => ({
    ...t_1,
    el: elById[t_1.id]
  })).filter(t_2 => t_2.el != null && (t_2.el.checkVisibility ? t_2.el.checkVisibility() : true)),
  // some older browsers don't support checkVisibility
  [tipsList_0, elById]);
  useEffect(() => {
    if (validTipIds.length > 0 && loadState == 'wait-for-el') setLoadState('enable');
  }, [validTips, loadState]);

  // when we get new valid tips appear, open it again
  const validTipIds = useMemoValue(validTips.map(t_3 => t_3.id));
  useEffect(() => {
    if (validTipIds.length > 0 && openState == false) setOpen(undefined);
  }, [validTipIds]);
  const disabled = openState == false || options?.disabled || loadState != 'enable' || options.settings.noTips == true;
  const tip = validTips.find(({
    seen
  }) => !seen);
  const scrollOnNextTipChange = useRef(false);
  const next = (mode: 'next' | 'prev' | 'ok' = 'ok') => {
    if (!tip) return;
    if (mode == 'prev') {
      const prevTip = validTips[validTips.indexOf(tip) - 1];
      if (prevTip) {
        setShownTips(s => s.filter(t_4 => t_4 != prevTip.id));
        options?.onSeen?.(prevTip.id, 'un-see');
      }
    } else {
      setShownTips(s_0 => [...s_0, tip.id]);
    }
    if (mode == 'ok') options?.onSeen?.(tip.id, 'seen');
    scrollOnNextTipChange.current = true;
  };
  useEffect(() => {
    if (!scrollOnNextTipChange.current || !tip?.el) return;
    scrollOnNextTipChange.current = false;
    tip.el.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
    scrollIntoViewWidthPad(tip.el, {
      behavior: 'smooth',
      block: 'nearest',
      pad: '10em'
    });
  }, [tip?.el]);
  const reset = () => {
    setShownTips([]);
    setOpen(undefined);
    setInitialSeen([]);
    options.onReset();
  };
  const close = () => {
    setShownTips(s_1 => unique([...s_1, ...validTips.map(t_5 => t_5.id)])); // when the user closes the tour, don't show those gain. But show new ones
    setOpen(false);
  };
  const toSee = validTips.filter(t_6 => !t_6.seen && t_6.id !== tip?.id).length;
  const {
    onDisable
  } = options ?? {};
  return {
    validTips,
    tips,
    tip,
    next,
    reset,
    close,
    toSee,
    disabled,
    onDisable,
    settings,
    cfg: tourCfg
  };
};
export const Tour = <TipId extends string,>({
  tour
}: {
  tour: UseTour<TipId>;
}) => {
  const arrowRef = useRef(null);
  const {
    tip,
    next,
    validTips,
    close,
    onDisable,
    disabled,
    settings
  } = tour;
  const {
    refs,
    floatingStyles,
    context,
    update
  } = useFloating({
    open: !!tip,
    whileElementsMounted: autoUpdate,
    middleware: [offset(10), flip(), shift({
      padding: 4
    }), arrow({
      element: arrowRef,
      padding: 4
    })],
    placement: tip?.placement
  });
  useEffect(() => {
    if (!tip?.el) return;
    refs.setReference(tip.el);
    update();
  }, [tip?.el]);
  useHotkeys([['Escape' as KeyboardEventKey, close]]);
  const seenCount = validTips.filter(t => t.seen).length;
  const visible = tip && !disabled;
  const [preAnimateDone, setPreAnimateDone] = useState(false);
  useEffect(() => {
    if (visible) setTimeout(() => setPreAnimateDone(true), 500);else setPreAnimateDone(false);
  }, [visible]);

  /** gets the effective z index of an element so we can place the tip just above it */
  const getZIndex = (el: Element | null | undefined): number | undefined => el == null ? undefined : pipe(window.getComputedStyle(el).getPropertyValue('z-index'), z => parseInt(z), z_0 => isNaN(z_0) ? undefined : z_0) ?? getZIndex(el.parentElement);
  const zIndex = getZIndex(tip?.el) ?? 100;
  return visible ? <DarkSchemedChildren>
      <div className='bg-[--mantine-color-body] text-[--mantine-color-text] shadow-lg rounded-md px-[1em] pt-2 pb-2 flex flex-col transition max-w-[min(35em,calc(100vw-8px))]' ref={refs.setFloating} style={{
      ...floatingStyles,
      opacity: preAnimateDone ? 1 : 0,
      minWidth: tip.video ? 'min(30em,100vw)' : '20em',
      zIndex
    }}
    // mantine popovers listen for onMouseDown and onTouchStart. Stop propagating outside the tour tip because we don't clicking anything in the tour to close open popovers
    onMouseDown={e => e.stopPropagation()} onTouchStart={e_0 => e_0.stopPropagation()}>
        <TipContent tip={tip} onClose={close} classNames={{
        videoContainer: 'mx-[-1em]'
      }} />
        <div className='mt-4 flex items-center gap-2'>
          <div className='flex items-center'>
            {<ActionIcon style={{
            display: seenCount > 0 ? undefined : 'none'
          }} onClick={() => next('prev')}>
                <HiChevronLeft />
              </ActionIcon>}
            <div className='opacity-70 text-nowrap'>
              <b>{seenCount + 1}</b> of <b>{validTips.length}</b>
            </div>
            <ActionIcon style={{
            display: seenCount + 1 < validTips.length ? undefined : 'none'
          }} className='' onClick={() => next('next')}>
              <HiChevronRight />
            </ActionIcon>
          </div>
          <Button className='ml-auto' onClick={onDisable}>
            Don't show tips
          </Button>
          <Button variant='filled' onClick={e_1 => {
          e_1.stopPropagation();
          e_1.preventDefault();
          next('ok');
        }}>
            Got it
          </Button>
        </div>
        <FloatingArrow ref={arrowRef} context={context} />
      </div>
    </DarkSchemedChildren> : null;
};
const TipContent = ({
  tip,
  onClose,
  hideHeading,
  classNames
}: {
  tip: TipMd;
  onClose?: () => void;
  hideHeading?: Boolean;
  classNames?: {
    videoContainer?: string;
    video?: string;
  };
}) => {
  const [isMuted, setIsMuted] = useSessionStorage({
    key: 'tour-video-muted',
    defaultValue: true
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleVolumeChange = () => setIsMuted(video.muted);
    video.addEventListener('volumechange', handleVolumeChange);
    return () => {
      video.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [videoRef.current]);
  return <>
      {!hideHeading && <div className='flex items-center gap-2'>
          <div className='font-bold mr-auto'>{tip.title}</div>
          {tip.more && <Button component='a' className=' !text-[--mantine-color-text]' target='_help' href={nav.help.url.urlSansQuery + tip.more} rightSection={<HiExternalLink />}>
              More
            </Button>}
          {onClose ? <CloseButton onClick={onClose} /> : null}
        </div>}
      <Markdown>{tip.text}</Markdown>
      {tip.video && <div className={twMerge('', classNames?.videoContainer)}>
          <video ref={videoRef} className={twMerge('w-full max-h-[400px]', classNames?.video)} //
      autoPlay playsInline muted={isMuted} controls crossOrigin='anonymous' src={nav.helpAsset.url.addPath(tip.video).url}>
            <track default kind='subtitles' srcLang='en' className='text-sm' src={nav.helpAsset.url.addPath(tip.video.replace(/\.mp4$/, '.vtt')).url} />
          </video>
        </div>}
    </>;
};
const moreUrl = (tip: Pick<TipMd, 'more'>) => isNullOrEmpty(tip.more) ? undefined : `${nav.help.url.urlSansTrailingSlash}/${tip.more}`;

/** Display relevant tour info hovering on an info element
 * for ergonomic reasons, this control references the global cfg directly */
export const TourInfo = t0 => {
  const $ = _c(24);
  let props;
  let tipsRaw;
  if ($[0] !== t0) {
    const {
      tips: t1,
      style,
      ...t2
    } = t0;
    tipsRaw = t1;
    props = t2;
    $[0] = t0;
    $[1] = props;
    $[2] = tipsRaw;
  } else {
    props = $[1];
    tipsRaw = $[2];
  }
  let t1;
  if ($[3] !== tipsRaw) {
    t1 = match(tipsRaw).with(P.array(), _temp9).otherwise(_temp10);
    $[3] = tipsRaw;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const multiTab = t1;
  const tipStore = useTip();
  const t2 = !multiTab;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      tooltip: {
        padding: 0
      }
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = multiTab ? "h-[min(50vh,40em)]" : "max-h-[40em]";
  let t5;
  if ($[6] !== t4) {
    t5 = twMerge("w-[min(100vw,45em)]", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== multiTab || $[9] !== tipStore || $[10] !== tipsRaw) {
    let t7;
    if ($[12] !== multiTab) {
      t7 = ts_0 => {
        const tips = ts_0.map(_temp11);
        return <Tabs defaultValue={ts_0[0]} keepMounted={false} classNames={{
          root: twMerge("w-full !flex !flex-col"),
          panel: "overflow-y-auto flex-grow flex flex-col px-[12px]",
          tab: "!text-[--mantine-color-dimmed] data-[active=true]:!text-[--mantine-color-text]",
          tabLabel: "flex flex-row items-center gap-1 min-h-[1.5em]"
        }}>{multiTab && <Tabs.List>{tips.map(_temp12)}</Tabs.List>}{tips.map(_temp13)}</Tabs>;
      };
      $[12] = multiTab;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== tipStore) {
      t8 = t_0 => <div className="overflow-y-auto flex-grow flex flex-col px-[12px]"><TipContent tip={pipe(tourCfg[t_0], cfg => ({
          ...(cfg as TipMd),
          id: t_0
        }))} classNames={{
          videoContainer: "flex grow items-center border-blue-200 mx-[-11px] min-h-[25em]"
        }} onClose={() => tipStore.setOpened(false)} /></div>;
      $[14] = tipStore;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t6 = match(tipsRaw).with(P.array(), t7).otherwise(t8);
    $[8] = multiTab;
    $[9] = tipStore;
    $[10] = tipsRaw;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== props || $[20] !== t2 || $[21] !== t7 || $[22] !== tipStore) {
    t8 = <InfoTip store={tipStore} hideClose={t2} withinPortal={false} position="bottom" styles={t3} {...props} label={t7} />;
    $[19] = props;
    $[20] = t2;
    $[21] = t7;
    $[22] = tipStore;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
export const TourContext = createContext<UseTour<keyof typeof tourCfg>>({} as any);
export const useTourContext = () => {
  return useContext(TourContext);
};
function _temp9(ts) {
  return ts.length > 1;
}
function _temp10() {
  return false;
}
function _temp11(t) {
  return {
    ...(tourCfg[t] as TipMd),
    id: t
  };
}
function _temp12(tip) {
  return <Tabs.Tab value={tip.id} key={tip.id}><div className="font-semibold">{tip.title}</div>{tip.more && <Tooltip label="More help"><div><ActionIcon component="a" href={moreUrl(tip)} target="_help"><HiExternalLink /></ActionIcon></div></Tooltip>}</Tabs.Tab>;
}
function _temp13(tip_0) {
  return <Tabs.Panel value={tip_0.id} key={tip_0.id}><TipContent tip={tip_0} hideHeading={true} classNames={{
      videoContainer: "flex grow items-center border-blue-200 mx-[-11px] min-h-[25em]"
    }} /></Tabs.Panel>;
}